import api from '../../api';
import { LOCALE_CODE_STRAPI } from '../../constants/locales';
import { STRAPI_ATTRIBUTES_ACTIVITY_DISCIPLINE } from '../../constants/strapiAttributes';
import { fetchStrapiData } from '../../util/api';
import { parseSelectFilterOptions } from '../../util/search';
import { parseListingToSharetribe } from '../../util/sharetribeParser';
import { LISTING_STATE_PUBLISHED } from '../../util/types';
import { fetchStrapiDataSSR } from '../../util/strapiHelpers';
import { getlistingFilters } from './SeoPage.helpers';

// Action types
export const FETCH_SEO_PAGE_REQUEST = 'app/SeoPage/FETCH_SEO_PAGE_REQUEST';
export const FETCH_SEO_PAGE_SUCCESS = 'app/SeoPage/FETCH_SEO_PAGE_SUCCESS';
export const FETCH_SEO_PAGE_ERROR = 'app/SeoPage/FETCH_SEO_PAGE_ERROR';

export const QUERY_SEO_REQUEST = 'app/SeoPage/QUERY_SEO_REQUEST';
export const QUERY_SEO_SUCCESS = 'app/SeoPage/QUERY_SEO_SUCCESS';
export const QUERY_SEO_ERROR = 'app/SeoPage/QUERY_SEO_ERROR';

export const QUERY_POPULAR_LISTINGS_REQUEST = 'app/SeoPage/QUERY_POPULAR_LISTINGS_REQUEST';
export const QUERY_POPULAR_LISTINGS_SUCCESS = 'app/SeoPage/QUERY_POPULAR_LISTINGS_SUCCESS';
export const QUERY_POPULAR_LISTINGS_ERROR = 'app/SeoPage/QUERY_POPULAR_LISTINGS_ERROR';

// Action to clear old data when page/language changes
export const CLEAR_CSR_DATA = 'app/SeoPage/CLEAR_CSR_DATA';

// Initial state
const initialState = {
  seoPageData: null,
  fetchInProgress: false,
  fetchError: null,
  seoData: null,
  querySeoInProgress: false,
  querySeoError: null,
  queryPopularListingsInProgress: false,
  queryPopularListingsError: null,
  popularListings: [],
  localizedUrlMappings: {}, // Store locale -> urlPath mappings
};

// Reducer
export default function seoPage(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH_SEO_PAGE_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };

    case FETCH_SEO_PAGE_SUCCESS:
      return { 
        ...state, 
        fetchInProgress: false, 
        seoPageData: payload.data?.[0] || payload,
        localizedUrlMappings: payload.localizedUrlMappings || {},
      };

    case FETCH_SEO_PAGE_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };

    case QUERY_POPULAR_LISTINGS_REQUEST:
      return {
        ...state,
        queryPopularListingsInProgress: true,
        queryPopularListingsError: null,
      };
    case QUERY_POPULAR_LISTINGS_SUCCESS:
      return {
        ...state,
        queryPopularListingsInProgress: false,
        popularListings: payload?.listings,
      };
    case QUERY_POPULAR_LISTINGS_ERROR:
      return {
        ...state,
        queryPopularListingsInProgress: false,
        queryPopularListingsError: payload,
      };
    case QUERY_SEO_REQUEST:
      return {
        ...state,
        querySeoInProgress: true,
        querySeoError: null,
      };
    case QUERY_SEO_SUCCESS:
      return {
        ...state,
        seoData: payload || [],
        querySeoInProgress: false,
      };
    case QUERY_SEO_ERROR:
      return {
        ...state,
        querySeoInProgress: false,
        querySeoError: payload,
      };
    case CLEAR_CSR_DATA:
      return {
        ...state,
        seoData: null,
        popularListings: [],
        querySeoInProgress: false,
        queryPopularListingsInProgress: false,
        querySeoError: null,
        queryPopularListingsError: null,
      };
    default:
      return state;
  }
}

// Action creators
export const fetchSeoPageRequest = () => ({
  type: FETCH_SEO_PAGE_REQUEST,
});

export const fetchSeoPageSuccess = data => ({
  type: FETCH_SEO_PAGE_SUCCESS,
  payload: data,
});

export const fetchSeoPageError = error => ({
  type: FETCH_SEO_PAGE_ERROR,
  payload: error,
});

export const seoRequest = () => ({
  type: QUERY_SEO_REQUEST,
});

export const seoSuccess = response => ({
  type: QUERY_SEO_SUCCESS,
  payload: response,
});

export const seoError = e => ({
  type: QUERY_SEO_ERROR,
  error: true,
  payload: e,
});

export const queryPopularListingsRequest = () => ({
  type: QUERY_POPULAR_LISTINGS_REQUEST,
});

export const queryPopularListingsSuccess = listings => ({
  type: QUERY_POPULAR_LISTINGS_SUCCESS,
  payload: { listings },
});

export const queryPopularListingsError = e => ({
  type: QUERY_POPULAR_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const clearCSRData = () => ({
  type: CLEAR_CSR_DATA,
});

// Fetch carousel data only (for related pages)
export const fetchCarouselData = (pageType, limit = 100, locale) => async dispatch => {
  dispatch(seoRequest());

  try {
    const response = await fetchStrapiData({
      category: 'pages',
      filters: {
        pageType: pageType,
        limit: limit,
        locale: locale, // Pass locale to filter articles by language
      },
    });

    const carouselData = response?.data || [];
    dispatch(seoSuccess(carouselData));
  } catch (error) {
    dispatch(seoError(error));
  }
};

// Thunk for querying popular listings
export const queryPopularListings = params => async (dispatch, getState, sdk) => {
  dispatch(queryPopularListingsRequest());

  const { partner } = getState().ui;

  const publishedListings = { 'attributes.state': { $eq: LISTING_STATE_PUBLISHED } };
  const mostRecent = { $sort: '-attributes.createdAt' };

  // Build filters based on different parameters
  let filters = {};

  // Discipline filter (existing logic)
  if (params.slug) {
    filters['attributes.publicData.discipline'] = {
      $in: parseSelectFilterOptions(STRAPI_ATTRIBUTES_ACTIVITY_DISCIPLINE[(params?.slug)]),
    };
  }

  // Region filter - search in multiLangAddressParts.english.region (case-insensitive)
  if (params.region) {
    filters['attributes.publicData.multiLangAddressParts.english.region'] = {
      $regex: `^${params.region.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`,
      $options: 'i'
    };
  }

  // Country filter - search in multiLangAddressParts.english.country (case-insensitive)
  if (params.country) {
    filters['attributes.publicData.multiLangAddressParts.english.country'] = {

      $regex: `^${params.country.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`,
      $options: 'i'
    };
  }

  const filteredParams = {
    ...publishedListings,
    ...mostRecent,
    ...filters,
  };

  const fallbackParams = {
    ...publishedListings,
    ...mostRecent,
  };

  try {
    const response = await api.listings.fetchListings(filteredParams, partner?.key);
    let { items } = response.data;

    if (items.length === 0 && Object.keys(filters).length > 0) {
      const fallbackResponse = await api.listings.fetchListings(fallbackParams, partner?.key);
      items = fallbackResponse.data.items;
    }

    const listings = items.map(listing => parseListingToSharetribe(listing, listing.author));
    dispatch(queryPopularListingsSuccess(listings));
  } catch (e) {
    dispatch(queryPopularListingsError(e));
    throw e;
  }
};

// SSR loadData - gets everything in one call including localized URL mappings
export const loadData = context => async dispatch => {
  dispatch(fetchSeoPageRequest());

  try {
    const fullPath =
      context?.originalUrl ||
      context?.req?.originalUrl ||
      (typeof window !== 'undefined' ? window.location.pathname : '/');

    const segments = fullPath.replace(/^\/+/, '').split('/');
    if (segments.length < 2) {
      const error = new Error(`Invalid URL format. Expected /locale/urlPath, got: ${fullPath}`);
      error.status = 404;
      dispatch(fetchSeoPageError(error.message));
      throw error;
    }

    const locale = segments[0];
    const urlPath = segments.slice(1).join('/');
    const strapiLocale = LOCALE_CODE_STRAPI[locale] || locale;

  // SSR: loadData invoked for SEO page

    // ✅ PARTNER SUBDOMAIN VALIDATION: Block SEO pages on partner subdomains
    // SEO pages should ONLY be accessible on core domains (crion.org, test.crion.org, localhost)
    const isSSR = typeof window === 'undefined';
    if (isSSR) {
      const hostname = context?.hostname || context?.req?.hostname;
      
      if (hostname) {
        const parts = hostname.split('.');
        // Core domains: crion.org, test.crion.org, localhost
        const isCoreDomain =
          (parts.length === 2 && parts[0] === 'crion' && parts[1] === 'org') ||
          (parts.length === 3 && parts[0] === 'test' && parts[1] === 'crion' && parts[2] === 'org') ||
          (parts.length === 1 &&
            (parts[0] === 'localhost' ||
              parts[0].startsWith('localhost') ||
              parts[0] === '127.0.0.1' ||
              parts[0] === '::1' ||
              parts[0] === '[::1]'));

        if (!isCoreDomain) {
          // This is a partner subdomain (e.g., partner.crion.org, partner.test.crion.org, partner.localhost)
          // Partner subdomain detected - throw 404
          const error = new Error('SEO pages not available on partner subdomains');
          error.status = 404;
          dispatch(fetchSeoPageError(error.message));
          throw error;
        }

  // Hostname validated as core domain
      }
    }

    const pageResponse = await fetchStrapiDataSSR(
      {
        category: 'pages',
        filters: {
          urlPath: urlPath,
          locale: strapiLocale,
        },
      },
      { serverPort: context?.port }
    );

    // Received page response from Strapi

    // Extract the main page data
    const pageData = pageResponse?.data?.[0] || null;
    
    // ✅ CRITICAL: If no data from Strapi, this URL doesn't exist → 404
    if (!pageData) {
      const error = new Error(`SEO page not found: ${urlPath}`);
      error.status = 404;
      dispatch(fetchSeoPageError(error.message));
      throw error;
    }

    dispatch(fetchSeoPageSuccess(pageResponse));

    // SSR: fetch popular listings so listing sections and hidden navs
    // render with data in the initial HTML. We mirror the LandingPage
    // pattern: dispatch the queryPopularListings thunk and await it.
    try {
      const pageAttrs = pageData?.attributes || pageData || {};
      const pageType = pageAttrs.pageType;
      const tags = pageAttrs.tag || pageData.tag || [];

      const listingFilters = getlistingFilters(pageType, tags || []);

      const queryParams = {};
      if (listingFilters.discipline) {
        queryParams.slug = listingFilters.discipline;
      } else if (listingFilters.region) {
        queryParams.region = listingFilters.region;
      } else if (listingFilters.country) {
        queryParams.country = listingFilters.country;
      }

      // Await dispatch so Redux store is populated before SSR returns
      await dispatch(queryPopularListings(queryParams));
    } catch (err) {
      // Do not fail SSR on listing fetch errors; log for visibility
      console.error('SSR: Failed fetching popular listings for SEO page:', err && err.message ? err.message : err);
    }

    return pageData;
    
  } catch (e) {
    // Re-throw 404/410 errors so server returns proper status code
    if (e.status === 404 || e.status === 410) {
      throw e;
    }
    
    // For other errors, log and re-throw
    console.error('SSR: Unexpected error loading SEO page:', e);
    dispatch(fetchSeoPageError(e.message || e));
    throw e;
  }
};

// Selector helpers for URL mappings (localized versions are included in urlMappings)
export const getLocalizedUrl = (localizedUrlMappings, targetLocale) => {
  if (!localizedUrlMappings || typeof localizedUrlMappings !== 'object') {
    return null;
  }
  
  return localizedUrlMappings[targetLocale] || null;
};

export const getAllLocalizedUrls = (localizedUrlMappings) => {
  if (!localizedUrlMappings || typeof localizedUrlMappings !== 'object') {
    return {};
  }
  
  return { ...localizedUrlMappings };
};

// Helper to build full URLs for language switching
export const buildLocalizedUrls = (localizedUrlMappings, baseUrl = '') => {
  const urls = {};
  
  Object.entries(localizedUrlMappings).forEach(([locale, urlPath]) => {
    urls[locale] = `${baseUrl}/${locale}/${urlPath}`;
  });
  
  return urls;
};
